import * as React from 'react';
import type { Editor } from '@tiptap/react';

import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@cotera/client/v0/components/ui/tabs';
import { cn } from '@cotera/client/v0/lib/utils';
import { Icon } from '../../../../ui/icon/icon';
import type { CommentAuthor } from '../../../../ui/tiptap/extensions/comment-mark';
import { CommentByline } from './comment-byline';
import { scrollToCommentThread } from './comment-navigation';
import { useCommentThreadActions } from './comment-thread-actions';
import { CommentThreadPanel } from './comment-thread-panel';
import {
  useActiveCommentId,
  useCommentThreads,
  type CommentThreadSummary,
} from './comment-threads';

type Filter = 'open' | 'resolved';

const FILTERS: ReadonlyArray<{ key: Filter; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
];

function isFilter(value: string): value is Filter {
  return value === 'open' || value === 'resolved';
}

/**
 * One thread in the index: the words it is attached to, and the latest thing said
 * about them. Enough to recognise a conversation, not enough to hold it.
 */
function ThreadListRow({
  thread,
  active,
  onSelect,
}: {
  thread: CommentThreadSummary;
  active: boolean;
  onSelect: () => void;
}) {
  const lastReply = thread.replies[thread.replies.length - 1];
  const rowRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (active) {
      rowRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  return (
    <button
      ref={rowRef}
      type="button"
      onClick={onSelect}
      className={cn(
        'group/row flex w-full flex-col gap-1.5 rounded-lg border p-3 text-left transition-colors',
        // The rail is a white card, so a white card per row would be an edge drawn
        // around nothing. Rows are the surface itself and pick up a tint on hover;
        // the one the caret is in is marked by the same accent as the highlight in
        // the prose, which is the pairing the row is there to make.
        active
          ? 'border-[color:var(--comment-rule)] bg-muted/25'
          : 'border-transparent hover:bg-muted/40'
      )}
    >
      {thread.anchorText !== '' ? (
        <p className="truncate border-l-2 border-[color:var(--comment-rule)] pl-2 text-xs italic text-muted-foreground">
          &ldquo;{thread.anchorText}&rdquo;
        </p>
      ) : null}
      {lastReply !== undefined ? (
        <CommentByline reply={lastReply} compact>
          <p className="line-clamp-2 text-sm text-foreground/90">
            {lastReply.body}
          </p>
          {thread.replies.length > 1 ? (
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {thread.replies.length} replies
            </p>
          ) : null}
        </CommentByline>
      ) : null}
      {/* On approach only. A row is a preview of a conversation and reads as one
          without being told it is clickable; the hint is for the moment someone is
          deciding, and a permanent one on every row would be the same four words
          repeated down the column. */}
      <span className="flex items-center justify-end gap-0.5 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100">
        Open thread
        <Icon icon="chevron-right" className="h-3 w-3" />
      </span>
    </button>
  );
}

export interface CommentsRailProps {
  /** The editor whose threads this rail indexes. Null while it is still mounting. */
  editor: Editor | null;
  author: CommentAuthor | null;
  /** The thread being read, or null for the list. Held by the rail's provider. */
  openThreadId: string | null;
  onOpenThread: (commentId: string | null) => void;
  onClose: () => void;
}

/**
 * An index of the document's comments: every thread, open or resolved, in document
 * order.
 *
 * Two views in one column: the list, and one thread opened from it. Picking a row
 * moves the caret into the thread — which scrolls the prose to it and lights up the
 * words — and opens the conversation here, with "All discussions" as the way back.
 *
 * The thread is {@link CommentThreadPanel}, the same card the document shows in its
 * margin, not a second conversation UI. An earlier version of this rail had its own,
 * with its own composer, resolve and delete, and keeping two implementations of one
 * thing in step is a losing game. The rail supplies a header and a way back; what is
 * under it is the component the rest of the feature already uses.
 *
 * The list is the job the margin genuinely cannot do at every width. Chips beside a
 * line say a thread exists but not what it says, and only for the lines you have
 * scrolled to; resolved threads draw no highlight worth finding and no chip at all, so
 * this is the only place they can be reached.
 */
export function CommentsRail({
  editor,
  author,
  openThreadId,
  onOpenThread,
  onClose,
}: CommentsRailProps) {
  const [filter, setFilter] = React.useState<Filter>('open');
  const { threads, counts } = useCommentThreads(editor);
  const activeCommentId = useActiveCommentId(editor);
  const actions = useCommentThreadActions(editor, author);

  const openThread =
    threads.find((thread) => thread.commentId === openThreadId) ?? null;

  // Deleting the thread being read, or losing it to an undo, returns to the list
  // rather than leaving the column on a conversation the document no longer has.
  React.useEffect(() => {
    if (openThreadId !== null && openThread === null) {
      onOpenThread(null);
    }
  }, [onOpenThread, openThread, openThreadId]);

  const activeThread = threads.find(
    (thread) => thread.commentId === activeCommentId
  );

  // Follow the caret across the tabs, so selecting a resolved comment in the prose
  // does not leave the index sitting on a list it is missing from.
  React.useEffect(() => {
    if (activeThread !== undefined) {
      setFilter(activeThread.resolved ? 'resolved' : 'open');
    }
  }, [activeThread]);

  const visible = threads.filter((thread) =>
    filter === 'open' ? !thread.resolved : thread.resolved
  );

  return (
    <aside
      aria-label="Comments"
      // The card itself — white, rounded and bordered exactly like the artifact
      // beside it. The chrome is `[data-slot='comments-rail']` in `v1.css`, sharing
      // one rule with the artifact panel so the two edges cannot drift apart.
      data-slot="comments-rail"
      className="pointer-events-auto flex h-full min-h-0 flex-1 flex-col overflow-hidden"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-border/50 px-3 py-2.5">
        <Icon
          icon="message-square-more"
          className="h-4 w-4 text-muted-foreground"
        />
        <h2 className="flex-1 truncate text-sm font-medium">Comments</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Close comments"
          className="size-7 shrink-0 p-0"
          onClick={onClose}
        >
          <Icon icon="x" className="h-4 w-4" />
        </Button>
      </div>

      {openThread !== null ? (
        <>
          {/* Named for where it goes, not for what you are leaving. "All discussions"
              says the list is still there and complete; a bare back arrow beside the
              word "Thread" only says you are somewhere else. */}
          <button
            type="button"
            onClick={() => onOpenThread(null)}
            className="flex shrink-0 items-center gap-1.5 border-b border-border/50 px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <Icon icon="arrow-left" className="h-3.5 w-3.5" />
            All discussions
          </button>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <CommentThreadPanel
              // A fresh draft per thread.
              key={openThread.commentId}
              thread={openThread}
              author={author}
              canEdit={editor?.isEditable === true && author !== null}
              onReply={(body) =>
                actions.addReply(openThread.commentId, openThread.replies, body)
              }
              onToggleResolved={() =>
                actions.toggleResolved(
                  openThread.commentId,
                  openThread.replies,
                  openThread.resolved
                )
              }
              onEditReply={(replyId, body) =>
                actions.editReply({
                  commentId: openThread.commentId,
                  replies: openThread.replies,
                  resolved: openThread.resolved,
                  replyId,
                  body,
                })
              }
              onDeleteReply={(replyId) =>
                actions.removeReply({
                  commentId: openThread.commentId,
                  replies: openThread.replies,
                  resolved: openThread.resolved,
                  replyId,
                })
              }
              // The rail is already a card; a second one inside it would be a frame
              // within a frame.
              className="rounded-none border-0 shadow-none"
            />
          </div>
        </>
      ) : (
        <>
          {/* The same segmented control the agent's Agent/Chats switch uses, from the
              same primitive rather than a hand-rolled copy of its look — which is how
              the two ended up with different corners in the first place. */}
          <Tabs
            value={filter}
            onValueChange={(value) => {
              if (isFilter(value)) {
                setFilter(value);
              }
            }}
            className="mx-3 my-2.5 shrink-0"
          >
            {/* A notch smaller than the primitive's default, to sit with "All
                discussions" and the rail's own heading rather than over them. This is
                a quiet control: it says which half of the list you are looking at,
                and the list is the thing being read. */}
            <TabsList className="h-8 w-full" aria-label="Comment status">
              {FILTERS.map(({ key, label }) => {
                const count = key === 'open' ? counts.open : counts.resolved;
                return (
                  <TabsTrigger key={key} value={key} className="text-xs">
                    {label}
                    {count > 0 ? ` ${count}` : ''}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
            {visible.length === 0 ? (
              <div className="flex flex-col items-center gap-1.5 px-4 py-10 text-center">
                <p className="text-sm font-medium text-foreground">
                  {filter === 'open'
                    ? 'No comments yet'
                    : 'No resolved comments'}
                </p>
                {/* Only on the Open tab. On Resolved the instruction would be
                answering a question nobody asked: how to start a conversation is
                not what an empty list of settled ones is about. */}
                {filter === 'open' ? (
                  <p className="text-xs text-muted-foreground">
                    Select text and click the comment icon to start a
                    conversation
                  </p>
                ) : null}
              </div>
            ) : (
              visible.map((thread) => (
                <ThreadListRow
                  key={thread.commentId}
                  thread={thread}
                  active={thread.commentId === activeCommentId}
                  onSelect={() => {
                    if (editor !== null) {
                      scrollToCommentThread(editor, thread.commentId);
                    }
                    onOpenThread(thread.commentId);
                  }}
                />
              ))
            )}
          </div>
        </>
      )}
    </aside>
  );
}
